import { useProductsList, type ProductItem } from '@/lib/api/products';
import React, { createContext, useContext } from 'react';
import { useParams } from 'react-router-dom';

interface ProductsContextValue {
  products: ProductItem[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

const ProductsContext = createContext<ProductsContextValue | undefined>(undefined);

interface ProductsProviderProps {
  children: React.ReactNode;
  storeId?: string;
}

export const ProductsProvider: React.FC<ProductsProviderProps> = ({ children, storeId: storeIdProp }) => {
  const { id } = useParams<{ id: string }>();
  const storeId = storeIdProp || id;

  if (!storeId) {
    throw new Error('Store id is not defined');
  }

  // Refetch products when provider mounts to ensure fresh data
  const {
    data,
    isLoading,
    error: queryError,
    refetch: refetchQuery,
  } = useProductsList(
    {
      storeId,
      page: null,
      includeVariants: true,
    },
    {
      refetchOnMount: 'always', // Always refetch when mounted
      refetchOnWindowFocus: false, // Don't refetch on window focus
    },
  );

  const products = data?.items ?? [];
  const loading = isLoading;
  const error = queryError ? new Error(queryError.message) : null;

  const refetch = async () => {
    await refetchQuery();
  };

  const value: ProductsContextValue = {
    products,
    loading,
    error,
    refetch,
  };

  return <ProductsContext.Provider value={value}>{children}</ProductsContext.Provider>;
};

export const useProductsContext = () => {
  const context = useContext(ProductsContext);

  if (!context) {
    throw new Error('useProductsContext must be used within a ProductsProvider');
  }

  return context;
};

/**
 * Safe version that returns null instead of throwing
 * Use this when the provider might not be available
 */
export const useProductsContextSafe = () => {
  const context = useContext(ProductsContext);
  return context || null;
};
